class CollectionFilters extends HTMLElement {
  constructor () {
    super()
    this.headers = []
    this.activeFilter = null
    this.handleFilterToggle = this.toggleFilter.bind(this)
    this.handleOutsideClick = this.handleOutsideClick.bind(this)
    this.setupListeners = this.setupListeners.bind(this)
  }

  connectedCallback () {
    this.setupListeners()
  }

  disconnectedCallback () {
    this.teardownListeners()
  }

  setupListeners () {
    this.teardownListeners()
    this.headers = Array.from(this.querySelectorAll('.js-filter-open'))
    this.headers.forEach(header => {
      header.addEventListener('click', this.handleFilterToggle)
    })
    if (!this.isSubCategoryVariant()) {
      document.addEventListener('click', this.handleOutsideClick)
    }
  }

  teardownListeners () {
    this.headers.forEach(header => {
      header.removeEventListener('click', this.handleFilterToggle)
    })
    this.headers = []
    document.removeEventListener('click', this.handleOutsideClick)
  }

  isSubCategoryVariant () {
    return this.dataset.collectionFiltersVariant === 'sub-category'
  }

  setHeaderExpandedState (header, isExpanded) {
    header.setAttribute('aria-expanded', String(isExpanded))
  }

  clearSubCategoryAnimationState (filterList) {
    if (!filterList) {
      return
    }

    if (filterList.animationFrameId) {
      window.cancelAnimationFrame(filterList.animationFrameId)
      filterList.animationFrameId = null
    }

    if (filterList.transitionFallbackFrameId) {
      window.cancelAnimationFrame(filterList.transitionFallbackFrameId)
      filterList.transitionFallbackFrameId = null
    }

    if (filterList.transitionEndHandler) {
      filterList.removeEventListener('transitionend', filterList.transitionEndHandler)
      filterList.transitionEndHandler = null
    }
  }

  parseTransitionTimeToMs (timeValue) {
    const parsedTime = Number.parseFloat(timeValue)

    if (Number.isNaN(parsedTime)) {
      return 0
    }

    return timeValue.includes('ms') ? parsedTime : parsedTime * 1000
  }

  forceReflow (element) {
    return element.offsetHeight
  }

  hasActiveSubCategoryHeightTransition (filterList) {
    const computedStyles = window.getComputedStyle(filterList)
    const transitionProperties = computedStyles.transitionProperty.split(',').map((value) => value.trim())
    const transitionDurations = computedStyles.transitionDuration.split(',').map((value) => this.parseTransitionTimeToMs(value.trim()))
    const transitionDelays = computedStyles.transitionDelay.split(',').map((value) => this.parseTransitionTimeToMs(value.trim()))

    return transitionProperties.some((property, index) => {
      const duration = transitionDurations[index % transitionDurations.length]
      const delay = transitionDelays[index % transitionDelays.length]

      return (property === 'all' || property === 'height') && (duration + delay) > 0
    })
  }

  onceSubCategoryHeightTransitionEnds (filterList, callback) {
    const handler = (event) => {
      if (event.target !== filterList || event.propertyName !== 'height') {
        return
      }

      filterList.removeEventListener('transitionend', handler)
      filterList.transitionEndHandler = null
      callback()
    }

    filterList.transitionEndHandler = handler
    filterList.addEventListener('transitionend', handler)

    if (!this.hasActiveSubCategoryHeightTransition(filterList)) {
      filterList.transitionFallbackFrameId = window.requestAnimationFrame(() => {
        if (filterList.transitionEndHandler !== handler) {
          return
        }

        filterList.removeEventListener('transitionend', handler)
        filterList.transitionEndHandler = null
        filterList.transitionFallbackFrameId = null
        callback()
      })
    }
  }

  openSubCategoryFilterList (filterList) {
    this.clearSubCategoryAnimationState(filterList)
    filterList.hidden = false
    filterList.classList.add('is-active')
    filterList.style.height = 'auto'

    const expandedHeight = `${filterList.scrollHeight}px`

    filterList.style.height = '0px'
    filterList.setAttribute('aria-hidden', 'false')
    this.forceReflow(filterList)

    filterList.animationFrameId = window.requestAnimationFrame(() => {
      filterList.style.height = expandedHeight
      filterList.animationFrameId = null
    })

    this.onceSubCategoryHeightTransitionEnds(filterList, () => {
      filterList.style.height = 'auto'
    })
  }

  closeSubCategoryFilterList (filterList) {
    this.clearSubCategoryAnimationState(filterList)

    if (filterList.hidden) {
      filterList.classList.remove('is-active')
      filterList.setAttribute('aria-hidden', 'true')
      filterList.style.height = ''
      return
    }

    filterList.style.height = `${filterList.getBoundingClientRect().height}px`
    this.forceReflow(filterList)
    filterList.classList.remove('is-active')
    filterList.setAttribute('aria-hidden', 'true')

    filterList.animationFrameId = window.requestAnimationFrame(() => {
      filterList.style.height = '0px'
      filterList.animationFrameId = null
    })

    this.onceSubCategoryHeightTransitionEnds(filterList, () => {
      filterList.hidden = true
      filterList.style.height = ''
    })
  }

  setFilterListExpandedState (filterList, isExpanded) {
    if (this.isSubCategoryVariant()) {
      if (isExpanded) {
        this.openSubCategoryFilterList(filterList)
      } else {
        this.closeSubCategoryFilterList(filterList)
      }
      return
    }

    filterList.classList.toggle('is-active', isExpanded)
    filterList.hidden = !isExpanded
    filterList.setAttribute('aria-hidden', String(!isExpanded))
  }

  getActiveFilterIds () {
    return this.headers
      .filter(header => header.classList.contains('is-active'))
      .map(header => header.id)
  }

  setActiveFilterIds (ids = []) {
    this.headers.forEach(header => {
      header.classList.remove('is-active')
      this.setHeaderExpandedState(header, false)

      const filterList = header.nextElementSibling
      if (filterList && filterList.classList.contains('collection__filters__single__list')) {
        this.setFilterListExpandedState(filterList, false)
      }
    })

    this.activeFilter = null

    ids.forEach((id) => {
      const header = this.querySelector(`#${id}`)
      const filterList = header && header.nextElementSibling

      if (!header || !filterList || !filterList.classList.contains('collection__filters__single__list')) {
        return
      }

      header.classList.add('is-active')
      this.setHeaderExpandedState(header, true)
      this.setFilterListExpandedState(filterList, true)
      this.activeFilter = filterList
    })
  }

  toggleFilter (event) {
    event.stopPropagation()

    const header = event.currentTarget
    const filterList = header.nextElementSibling

    if (filterList && filterList.classList.contains('collection__filters__single__list')) {
      if (this.activeFilter && this.activeFilter !== filterList) {
        this.setFilterListExpandedState(this.activeFilter, false)

        const previousHeader = this.activeFilter.previousElementSibling
        if (previousHeader && previousHeader.classList.contains('js-filter-open')) {
          previousHeader.classList.remove('is-active')
          this.setHeaderExpandedState(previousHeader, false)
        }
      }

      const isActive = !filterList.classList.contains('is-active')
      this.setFilterListExpandedState(filterList, isActive)
      header.classList.toggle('is-active', isActive)
      this.setHeaderExpandedState(header, isActive)

      this.activeFilter = isActive ? filterList : null
    }
  }

  handleOutsideClick (event) {
    if (this.activeFilter) {
      if (!this.contains(event.target)) {
        this.setFilterListExpandedState(this.activeFilter, false)

        const previousHeader = this.activeFilter.previousElementSibling
        if (previousHeader && previousHeader.classList.contains('js-filter-open')) {
          previousHeader.classList.remove('is-active')
          this.setHeaderExpandedState(previousHeader, false)
        }

        this.activeFilter = null
      }
    }
  }
}

customElements.define('collection-filters', CollectionFilters)
